import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export enum TrackingCategory {
  PAGE_VIEWS = 'page_views',
  BLOG = 'blog',
  BLOG_FILTER = 'blog_filter',
  BLOG_PAGINATION = 'blog_pagination',
  BLOG_SEARCH = 'blog_search',
  BLOG_SOCIAL = 'blog_social',
  USER_JOURNEY = 'user_journey',
  ENGAGEMENT = 'engagement',
  FORMS = 'forms',
  ERROR = 'error'
}

export interface EventParams {
  category?: TrackingCategory;
  action?: string;
  label?: string;
  value?: number;
  [key: string]: any;
}

export interface UTMParams {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term?: string;
  utm_content?: string;
  landing_page?: string;
  timestamp?: string;
}

export interface BlogPost {
  id: string;
  title: string;
  category: string;
  author: string;
  publishDate?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private readonly GA_TRACKING_ID = 'G-834D8WQXDD';
  private isInitialized = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private titleService: Title
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.initializeGoogleAnalytics();
      this.setupRouterEvents();
    }
  }

  private initializeGoogleAnalytics(): void {
    if (this.isInitialized) return;

    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${this.GA_TRACKING_ID}`;

    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };

    window.gtag('js', new Date());
    window.gtag('config', this.GA_TRACKING_ID, {
      send_page_view: false,
      transport_url: 'https://www.google-analytics.com',
      first_party_collection: true
    });

    document.head.appendChild(gtagScript);
    this.isInitialized = true;
  }

  private setupRouterEvents(): void {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.trackPageView({
          page_title: this.titleService.getTitle(),
          page_path: event.urlAfterRedirects
        });
      }
    });
  }

  // Core tracking methods
  trackPageView(params: EventParams = {}): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    window.gtag('event', 'page_view', {
      ...params,
      send_to: this.GA_TRACKING_ID
    });
  }

  trackEvent(category: TrackingCategory, action: string, params: EventParams = {}): void {
    if (!isPlatformBrowser(this.platformId)) return;

    window.gtag('event', `${category}_${action}`, {
      event_category: category,
      ...params,
      send_to: this.GA_TRACKING_ID
    });
  }

  // Blog tracking methods
  trackBlogView(post: BlogPost): void {
    this.trackEvent(TrackingCategory.BLOG, 'view', {
      post_id: post.id,
      post_title: post.title,
      post_category: post.category,
      author: post.author,
      publication_date: post.publishDate
    });
  }

  trackSearch(query: string, params: EventParams = {}): void {
    this.trackEvent(TrackingCategory.BLOG_SEARCH, 'query', {
      search_term: query,
      ...params
    });
  }

  trackSocialShare(platform: string, params: EventParams = {}): void {
    this.trackEvent(TrackingCategory.BLOG_SOCIAL, 'share', {
      platform,
      ...params
    });
  }

  // Engagement tracking methods
  trackEngagement(action: string, params: EventParams = {}): void {
    this.trackEvent(TrackingCategory.ENGAGEMENT, action, params);
  }

  trackScrollDepth(depth: number): void {
    this.trackEvent(TrackingCategory.ENGAGEMENT, 'scroll_depth', {
      value: depth,
      page_title: this.titleService.getTitle(),
      page_path: this.router.url
    });
  }

  // Form tracking methods
  trackFormSubmission(formId: string, success: boolean, params: EventParams = {}): void {
    this.trackEvent(TrackingCategory.FORMS, 'submission', {
      form_id: formId,
      success,
      ...params
    });
  }

  // Campaign tracking methods
  trackUTM(params: Partial<UTMParams>): void {
    if (!params.utm_source || !params.utm_medium || !params.utm_campaign) {
      console.warn('Missing required UTM parameters');
      return;
    }

    this.trackEvent(TrackingCategory.USER_JOURNEY, 'campaign_visit', {
      campaign_source: params.utm_source,
      campaign_medium: params.utm_medium,
      campaign_name: params.utm_campaign,
      campaign_term: params.utm_term,
      campaign_content: params.utm_content,
      landing_page: params.landing_page || this.router.url
    });
  }

  // Error tracking
  trackError(error: Error, params: EventParams = {}): void {
    this.trackEvent(TrackingCategory.ERROR, 'error', {
      error_name: error.name,
      error_message: error.message,
      ...params
    });
  }
} 